import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { CodeAIRequest, CodeAIResponse, ValidationResult } from '../../../models/code-ai/code-ai.model';

/**
 * Service for JavaScript/Script AI code generation
 */
@Injectable({
  providedIn: 'root'
})
export class ScriptAIService {
  private get baseUrl(): string {
    // Use the same API endpoint as other services
    return environment.api.API_ENDPOINT;
  }

  constructor(private http: HttpClient) {}

  /**
   * Generate JavaScript code based on natural language prompt and preference page ID
   * @param request Code AI request with preference page ID, CSS context and prompt
   * @returns Observable of generated script response
   */
  generateScript(request: Omit<CodeAIRequest, 'type' | 'html'> & { preferencePageId: string; existingCSS?: string }): Observable<CodeAIResponse> {
    const fullRequest: CodeAIRequest = {
      ...request,
      type: 'javascript',
      html: '' // No longer needed - backend generates from preferencePageId
    };

    // Add 'skip' header to bypass interceptor notifications
    const headers = { 'skip': 'true' };

    return this.http.post<CodeAIResponse>(
      `${this.baseUrl}/ai/generate-script`,
      fullRequest,
      { headers }
    ).pipe(
      map((response: any) => {
        // Handle response - don't force success property
        console.log('Script AI Response:', response);
        
        // If response is successful, ensure it has the expected structure
        if (response && (response.success || response.code)) {
          return {
            code: response.code || '',
            explanation: response.explanation || '',
            validation: response.validation || { isValid: true, warnings: [], securityIssues: [], syntaxErrors: [] },
            success: response.success !== false,
            error: response.error || null
          } as CodeAIResponse;
        }
        
        // If response indicates failure
        return {
          code: '',
          explanation: '',
          validation: { isValid: false, warnings: [], securityIssues: [], syntaxErrors: [] },
          success: false,
          error: response.error || 'Failed to generate script'
        } as CodeAIResponse;
      }),
      catchError((error: any) => {
        console.error('Script AI Generation Error:', error);
        
        // Return a proper error response instead of throwing
        return of({
          code: '',
          explanation: '',
          validation: { isValid: false, warnings: [], securityIssues: [], syntaxErrors: [] },
          success: false,
          error: error?.error?.message || error?.message || 'Failed to generate script'
        } as CodeAIResponse);
      })
    );
  }

  /**
   * Validate JavaScript code for security and syntax
   * @param script JavaScript code to validate
   * @returns Observable of validation result with security checks
   */
  validateScript(script: string): Observable<ValidationResult> {
    return this.http.post<ValidationResult>(
      `${this.baseUrl}/ai/validate-script`,
      { script }
    ).pipe(
      catchError((error: any) => {
        console.error('Script Validation Error:', error);
        return of({
          isValid: false,
          syntaxErrors: [error.message || 'Validation failed'],
          warnings: [],
          securityIssues: []
        });
      })
    );
  }

  /**
   * Get script suggestions based on preference page
   * @param preferencePageId ID of the preference page
   * @param css Optional CSS content for context
   * @returns Observable of script suggestions
   */
  getSuggestions(preferencePageId: string, css?: string): Observable<string[]> {
    return this.http.post<{ suggestions: string[] }>(
      `${this.baseUrl}/ai/script-suggestions`,
      { preferencePageId, css }
    ).pipe(
      map(response => response.suggestions || []),
      catchError((error: any) => {
        console.error('Script Suggestions Error:', error);
        return of([]);
      })
    );
  }

  /**
   * Check if script contains potentially dangerous patterns
   * @param script Script to check
   * @returns Observable of security analysis
   */
  checkSecurity(script: string): Observable<{
    isSafe: boolean;
    issues: string[];
    recommendations: string[];
  }> {
    return this.http.post<{
      isSafe: boolean;
      issues: string[];
      recommendations: string[];
    }>(
      `${this.baseUrl}/ai/script-security-check`,
      { script }
    ).pipe(
      catchError((error: any) => {
        console.error('Script Security Check Error:', error);
        return of({
          isSafe: false,
          issues: ['Security check failed'],
          recommendations: ['Manual review recommended']
        });
      })
    );
  }
}
